import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useLlmConfigs } from '@/providers';
import cleanEmptyValues from '@/util/cleanEmptyValues';
import CheckpointSelect from '@/components/shared/CheckpointSelect';
import TypeField from './TypeField';
import FamilyField from './FamilyField';
import SizeField from './SizeField';
import ReasoningParserField from './ReasoningParserField';
import ToolParserField from './ToolParserField';
import AdditionalKwargsField from './AdditionalKwargsField';
import LlmNameField from './LlmNameField';
import ConcurrentRequestsField from './ConcurrentRequestsField';
import MoeField from './MoeField';
import QuantizationField from './QuantizationField';
import { LLM_FAMILIES } from './llmFamilies';
import {
  isValidLlmName,
  stripDefaultLlmOverrides,
  transformCheckpoint,
} from './utils';

const EMPTY_CHECKPOINT = { id: '', as: '', public: false, name: '' };

function toConcurrentRequestsState(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
}

function isValidConcurrentRequests(value) {
  if (value === '' || value === null || value === undefined) {
    return true;
  }
  const str = String(value);
  return /^\d+$/.test(str) && parseInt(str, 10) >= 1;
}

function parseConcurrentRequests(value) {
  if (
    !isValidConcurrentRequests(value) ||
    value === '' ||
    value === null ||
    value === undefined
  ) {
    return '';
  }
  return parseInt(String(value), 10);
}

function getDisplayLlmValues(initial_values, llm_configs) {
  if (
    initial_values?.type &&
    initial_values?.family &&
    llm_configs &&
    Object.keys(llm_configs).length > 0
  ) {
    return stripDefaultLlmOverrides(initial_values, llm_configs);
  }
  return initial_values || {};
}

function applyLlmValuesToState(normalized, setters) {
  setters.setType(normalized.type || '');
  setters.setFamily(normalized.family || '');
  setters.setSize(normalized.size || '');
  setters.setReasoningParser(normalized.reasoning_parser || '');
  setters.setToolParser(normalized.tool_parser || '');
  setters.setAdditionalKwargs(normalized.additional_kwargs || '');
  setters.setName(normalized.name || '');
  setters.setMoe(Boolean(normalized.moe));
  setters.setQuantization(normalized.quantization || '');
  setters.setConcurrentRequests(
    toConcurrentRequestsState(normalized.concurrent_requests),
  );
}

function clearCustomFields(setters) {
  setters.setCheckpoint(EMPTY_CHECKPOINT);
  setters.setCheckpointError(false);
  setters.setMoe(false);
  setters.setQuantization('');
}

export default function EndpointLLM({
  initialValues,
  initialCheckpoints,
  onChange,
  onCheckpointChange,
  edit = false,
}) {
  const llm_configs = useLlmConfigs() || {};
  const display_initial_values = getDisplayLlmValues(
    initialValues,
    llm_configs,
  );

  const [type, setType] = useState(display_initial_values.type || '');
  const [family, setFamily] = useState(display_initial_values.family || '');
  const [size, setSize] = useState(display_initial_values.size || '');

  const [reasoning_parser, setReasoningParser] = useState(
    display_initial_values.reasoning_parser || '',
  );
  const [tool_parser, setToolParser] = useState(
    display_initial_values.tool_parser || '',
  );
  const [additional_kwargs, setAdditionalKwargs] = useState(
    display_initial_values.additional_kwargs || '',
  );
  const [name, setName] = useState(display_initial_values.name || '');
  const [moe, setMoe] = useState(Boolean(display_initial_values.moe));
  const [quantization, setQuantization] = useState(
    display_initial_values.quantization || '',
  );
  const [concurrent_requests, setConcurrentRequests] = useState(
    toConcurrentRequestsState(display_initial_values.concurrent_requests),
  );
  const [checkpoint, setCheckpoint] = useState(() =>
    transformCheckpoint(initialCheckpoints?.[0]),
  );
  const [checkpoint_error, setCheckpointError] = useState(false);

  const is_custom = family === LLM_FAMILIES.CUSTOM;
  const [advanced_expanded, setAdvancedExpanded] = useState(is_custom);

  const isInitialMount = useRef(true);
  const onChangeRef = useRef(onChange);
  const onCheckpointChangeRef = useRef(onCheckpointChange);
  const has_applied_config_strip_ref = useRef(false);

  const type_configs = useMemo(
    () => (type ? llm_configs?.[type] || {} : {}),
    [type, llm_configs],
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCheckpointChangeRef.current = onCheckpointChange;
  }, [onCheckpointChange]);

  useEffect(() => {
    if (is_custom) {
      setAdvancedExpanded(true);
    }
  }, [is_custom]);

  // When copying a job, strip override fields that match config defaults once configs load.
  useEffect(() => {
    if (has_applied_config_strip_ref.current) {
      return;
    }
    if (
      !initialValues?.type ||
      !initialValues?.family ||
      !llm_configs ||
      Object.keys(llm_configs).length === 0
    ) {
      return;
    }
    has_applied_config_strip_ref.current = true;
    const normalized = stripDefaultLlmOverrides(initialValues, llm_configs);
    applyLlmValuesToState(normalized, {
      setType,
      setFamily,
      setSize,
      setReasoningParser,
      setToolParser,
      setAdditionalKwargs,
      setName,
      setMoe,
      setQuantization,
      setConcurrentRequests,
    });
  }, [initialValues, llm_configs]);

  const type_error = !type;
  const family_error = !family;
  const size_error = !is_custom && !size;
  const name_error = is_custom
    ? !isValidLlmName(name)
    : Boolean(name) && !isValidLlmName(name);
  const concurrent_requests_error = !isValidConcurrentRequests(
    concurrent_requests,
  );
  const has_error =
    type_error ||
    family_error ||
    size_error ||
    name_error ||
    concurrent_requests_error;

  const payload = useMemo(() => {
    const llm = cleanEmptyValues({
      type,
      family,
      size: is_custom ? '' : size,
      reasoning_parser,
      tool_parser,
      additional_kwargs,
      name,
      moe: is_custom && moe ? true : '',
      quantization: is_custom ? quantization : '',
      concurrent_requests: parseConcurrentRequests(concurrent_requests),
    });
    return llm || {};
  }, [
    type,
    family,
    size,
    is_custom,
    reasoning_parser,
    tool_parser,
    additional_kwargs,
    name,
    moe,
    quantization,
    concurrent_requests,
  ]);

  const allowed_sizes = useMemo(() => {
    if (!family || is_custom) return [];
    return Object.keys(type_configs?.[family]?.sizes || {});
  }, [family, is_custom, type_configs]);

  useEffect(() => {
    if (
      is_custom ||
      !family ||
      !size ||
      !type ||
      Object.keys(type_configs).length === 0
    ) {
      return;
    }
    if (!allowed_sizes.includes(size)) {
      setSize('');
    }
  }, [family, allowed_sizes, size, type, type_configs, is_custom]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      onChangeRef.current(payload, has_error);
      return;
    }
    onChangeRef.current(payload, has_error);
  }, [payload, has_error]);

  useEffect(() => {
    if (!onCheckpointChangeRef.current) {
      return;
    }
    if (!is_custom) {
      onCheckpointChangeRef.current({}, false);
      return;
    }
    const has_id = Boolean(checkpoint.id);
    const model_payload = has_id ? { checkpoints: [checkpoint] } : {};
    const has_checkpoint_error = Boolean(checkpoint_error) || !has_id;
    onCheckpointChangeRef.current(model_payload, has_checkpoint_error);
  }, [is_custom, checkpoint, checkpoint_error]);

  const handleTypeChange = (new_type) => {
    setType(new_type);
    setFamily('');
    setSize('');
    clearCustomFields({
      setCheckpoint,
      setCheckpointError,
      setMoe,
      setQuantization,
    });
  };

  const handleFamilyChange = (new_family) => {
    setFamily(new_family);
    setSize('');
    if (new_family !== LLM_FAMILIES.CUSTOM) {
      clearCustomFields({
        setCheckpoint,
        setCheckpointError,
        setMoe,
        setQuantization,
      });
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='body1' fontWeight='medium' sx={{ mb: 1 }}>
          LLM Configuration
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TypeField
          value={type}
          onChange={handleTypeChange}
          disabled={edit}
          error={type_error}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <FamilyField
          value={family}
          onChange={handleFamilyChange}
          llm_configs={type_configs}
          disabled={edit || !type}
          error={family_error}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <SizeField
          value={size}
          onChange={setSize}
          llm_configs={type_configs}
          family={family}
          disabled={edit || is_custom}
          error={size_error}
        />
      </Grid>

      {is_custom ? (
        <Grid size={12}>
          <CheckpointSelect
            value={checkpoint}
            onChange={setCheckpoint}
            error={checkpoint_error}
            onError={setCheckpointError}
            disabled={edit}
            required
            field_id='endpoint-llm-checkpoint'
          />
        </Grid>
      ) : null}

      <Grid size={12}>
        <Accordion
          expanded={advanced_expanded}
          onChange={(_, expanded) => setAdvancedExpanded(expanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Advanced Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ReasoningParserField
                  value={reasoning_parser}
                  onChange={setReasoningParser}
                  disabled={edit}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ToolParserField
                  value={tool_parser}
                  onChange={setToolParser}
                  disabled={edit}
                />
              </Grid>
              <Grid size={12}>
                <AdditionalKwargsField
                  value={additional_kwargs}
                  onChange={setAdditionalKwargs}
                  disabled={edit}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LlmNameField
                  value={name}
                  onChange={setName}
                  disabled={edit}
                  required={is_custom}
                  error={name_error}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ConcurrentRequestsField
                  value={concurrent_requests}
                  onChange={setConcurrentRequests}
                  disabled={edit}
                  error={concurrent_requests_error}
                />
              </Grid>
              {is_custom ? (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <MoeField
                      value={moe}
                      onChange={setMoe}
                      disabled={edit}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <QuantizationField
                      value={quantization}
                      onChange={setQuantization}
                      disabled={edit}
                    />
                  </Grid>
                </>
              ) : null}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
}
